import {
  findNodePath,
  getAboveNode,
  selectEditor,
  setNodes,
} from 'lib-knowledge-document-structure/plate/common';
import {
  deleteColumn,
  deleteRow,
  getTableGridByRange,
  insertTableColumn,
  insertTableRow,
} from 'lib-knowledge-document-structure/plate/table';
import {
  ELEMENT_TABLE,
  ELEMENT_TD,
  ELEMENT_TH,
  ELEMENT_TR,
  MyEditor,
  MyTableCellElement,
  MyTableElement,
  MyTableRowElement,
} from 'lib-knowledge-document-structure/plateTypes';
import { Path } from 'lib-knowledge-document-structure/slate/package';

/**
 * Pass in a Row or Cell Element and it will return a tuple containing the table
 * element and the path to the table element.
 */
export function getParentTable({
  editor,
  element,
}: {
  editor: MyEditor;
  element: MyTableRowElement | MyTableCellElement;
}): [MyTableElement, Path] {
  const path = findNodePath(editor, element)!;
  return getAboveNode<MyTableElement>(editor, {
    match: { type: ELEMENT_TABLE },
    at: path,
  })!;
}

/** Does the table have a header row? ie all the cells in the first row are th */
export function getHasHeaderRow(element: MyTableElement): boolean {
  const firstRowChildren = element.children[0]?.children;
  if (!firstRowChildren?.length) return false;
  return firstRowChildren.every((c) => 'type' in c && c.type === ELEMENT_TH);
}

/**
 * Does the table have a column header? ie all the cells in the first column are
 * th
 */
export function getHasColumnHeader({
  editor,
  path,
  element,
}: {
  editor: MyEditor;
  path: Path;
  element: MyTableElement;
}): boolean {
  if (!element.children.length) return false;
  return getTableGridByRange(editor, {
    at: {
      anchor: { path: [...path, 0, 0], offset: 0 },
      focus: {
        path: [...path, element.children.length - 1, 0],
        offset: 0,
      },
    },
    format: 'cell',
  }).every((c) => {
    return 'type' in c[0] && c[0].type === ELEMENT_TH;
  });
}

/** Toggle on and off the table having a header row */
export function toggleTableHeader({
  editor,
  path,
  element,
}: {
  editor: MyEditor;
  path: Path;
  element: MyTableElement;
}): void {
  const hasHeaderRow = getHasHeaderRow(element);
  const hasColumnHeader = getHasColumnHeader({
    editor,
    path,
    element,
  });

  editor.withoutNormalizing(() => {
    setNodes(
      editor,
      { type: hasHeaderRow ? ELEMENT_TD : ELEMENT_TH },
      {
        at: {
          anchor: {
            path: [...path, 0, hasColumnHeader ? 1 : 0],
            offset: 0,
          },
          focus: {
            path: [...path, 0, element.children[0].children.length - 1],
            offset: 0,
          },
        },
        match: (node) =>
          'type' in node &&
          (node.type === ELEMENT_TD || node.type === ELEMENT_TH),
      },
    );
  });
}

/** Toggle on and off column headers for a table. */
export function toggleColumnHeader({
  editor,
  path,
  element,
}: {
  editor: MyEditor;
  path: Path;
  element: MyTableElement;
}): void {
  const hasHeaderRow = getHasHeaderRow(element);
  const hasColumnHeader = getHasColumnHeader({
    editor,
    path,
    element,
  });

  const cells = getTableGridByRange(editor, {
    at: {
      anchor: { path: [...path, hasHeaderRow ? 1 : 0, 0], offset: 0 },
      focus: {
        path: [...path, element.children.length - 1, 0],
        offset: 0,
      },
    },
    format: 'cell',
  });

  editor.withoutNormalizing(() => {
    cells.forEach((cell) => {
      setNodes(
        editor,
        { type: hasColumnHeader ? ELEMENT_TD : ELEMENT_TH },
        { at: cell[1] },
      );
    });
  });
}

/**
 * Pass in a path to a cell and get the path to the row it is located in.\
 * Rows are located on level above cells.
 */
export function cellPathToRowPath(path: Path): Path {
  return path.slice(0, -1);
}

/** Makes sure there is more than 1 row */
export function getCanDeleteRow({
  editor,
  element,
}: {
  editor: MyEditor;
  element: MyTableCellElement;
}): boolean {
  const currentTableItem = getParentTable({ editor, element });
  return !!currentTableItem && currentTableItem[0].children.length > 1;
}

/** Deletes the row at `rowPath` */
export function deleteTableRow({
  editor,
  rowPath,
  element,
}: {
  editor: MyEditor;
  rowPath: Path;
  element: MyTableCellElement;
}): void {
  if (!getCanDeleteRow({ editor, element }))
    throw new Error("Can't delete last row");

  selectEditor(editor, { at: rowPath });
  deleteRow(editor);
}

/** Makes sure there is more than one column in the table before you delete one */
export function getCanDeleteColumn({
  editor,
  path,
}: {
  editor: MyEditor;
  path: Path;
}): boolean {
  const currentRowItem = getAboveNode<MyTableRowElement>(editor, {
    match: { type: ELEMENT_TR },
    at: path,
  });
  return !!currentRowItem && currentRowItem[0].children.length > 1;
}

/** Removes the column where the cell at `path` is located */
export function deleteTableColumn({
  editor,
  path,
}: {
  editor: MyEditor;
  path: Path;
}): void {
  if (!getCanDeleteColumn({ editor, path }))
    throw new Error("Can't delete last column");
  selectEditor(editor, { at: path });
  deleteColumn(editor);
}

/** Removes all content in the row located at location `rowPath` */
export function clearRow({
  editor,
  rowPath,
}: {
  editor: MyEditor;
  rowPath: Path;
}): void {
  selectEditor(editor, { at: rowPath });
  deleteRow(editor);
  insertTableRow(editor, {
    at: rowPath,
  });
}

/** Removes all content the in column where the cell at path is located. */
export function clearColumn({
  editor,
  path,
}: {
  editor: MyEditor;
  path: Path;
}): void {
  selectEditor(editor, { at: path });
  deleteColumn(editor);
  insertTableColumn(editor, { at: path });
}

/**
 * Adds new row above the row at location `rowPath`. If the table has a header
 * row and we are adding above the first row, then the new row will also be a
 * header row.
 */
export function insertRowAbove({
  editor,
  element,
  rowPath,
}: {
  editor: MyEditor;
  element: MyTableCellElement;
  rowPath: Path;
}): void {
  const isFirstRow = rowPath[rowPath.length - 1] === 0;
  const table = getParentTable({ editor, element });
  const hasHeaderRow = getHasHeaderRow(table[0]);

  selectEditor(editor, { at: rowPath });
  editor.withoutNormalizing(() => {
    if (hasHeaderRow && isFirstRow) {
      toggleTableHeader({ editor, path: table[1], element: table[0] });
    }
    insertTableRow(editor, {
      at: rowPath,
      disableSelect: true,
      header: hasHeaderRow && isFirstRow ? true : undefined,
    });
  });
}

/** Adds a new row under the row at location `rowPath` */
export function insertRowBelow({
  editor,
  rowPath,
}: {
  editor: MyEditor;
  rowPath: Path;
}): void {
  selectEditor(editor, { at: rowPath });
  insertTableRow(editor, { fromRow: rowPath });
}

/**
 * Adds a new column before the cell at the location `path`, If header column is
 * present, then it will be taken into account.
 */
export function insertColumnBefore({
  editor,
  element,
  path,
}: {
  editor: MyEditor;
  element: MyTableCellElement;
  path: Path;
}): void {
  const isFirstColumn = path[path.length - 1] === 0;
  const table = getParentTable({ editor, element });
  const hasHeaderRow = getHasHeaderRow(table[0]);

  selectEditor(editor, { at: path });
  editor.withoutNormalizing(() => {
    if (hasHeaderRow && isFirstColumn) {
      toggleColumnHeader({ editor, path: table[1], element: table[0] });
    }
    insertTableColumn(editor, {
      at: path,
      header: hasHeaderRow && isFirstColumn ? true : undefined,
    });
  });
}

/** Adds a new column after the cell at the location `path` */
export function insertColumnAfter({
  editor,
  path,
}: {
  editor: MyEditor;
  path: Path;
}): void {
  selectEditor(editor, { at: path });
  insertTableColumn(editor, { fromCell: path });
}
